import type { EdgeRuntimeSnapshot } from "@sitescene/types";

import {
	type EdgeConfigurationDraft,
	edgeConfigurationDraftSchema,
} from "@sitescene/shared";

import { apiClient } from "@/api/client";

export interface ConfigurationPublication {
	draftVersion: number;
	publishedAt: string;
	publishedBy: string;
	revision: string;
}
export interface ConfigurationSnapshot {
	desiredRevision: null | string;
	draft: EdgeConfigurationDraft;
	installation: string;
	removals: {
		integrations: { id: string; name: string }[];
		sources: { id: string; name: string }[];
	};
	reports: EdgeRuntimeSnapshot[];
	sourceUrls: Record<string, string>;
	/** Sources whose stored camera URL the configuration key can't decrypt. */
	unreadableSourceUrls: string[];
	version: number;
}
export function configurationApi(installation: string, org: string) {
	const path = `/shadow/installations/${encodeURIComponent(installation)}/configuration`;
	const options = { headers: { "X-Shadow-Org": org } };
	const checkSnapshot = (value: ConfigurationSnapshot) => {
		edgeConfigurationDraftSchema.parse(value.draft);
		if (
			!Number.isInteger(value.version) ||
			value.version < 0 ||
			value.installation !== installation ||
			!value.sourceUrls ||
			!Array.isArray(value.unreadableSourceUrls) ||
			!value.removals ||
			!Array.isArray(value.removals.sources) ||
			!Array.isArray(value.removals.integrations) ||
			!Array.isArray(value.reports)
		)
			throw new Error("Incomplete configuration snapshot");
		for (const source of value.draft.sources) {
			if (
				source.fields.source_type === "rtsp_camera" &&
				source.secrets.rtsp_url?.operation === "keep" &&
				typeof value.sourceUrls[source.id] !== "string" &&
				!value.unreadableSourceUrls.includes(source.id)
			)
				throw new Error("Incomplete authoring configuration");
		}
		return value;
	};
	return {
		async history() {
			return (
				await apiClient.get<ConfigurationPublication[]>(
					`${path}/history`,
					options,
				)
			).data;
		},
		async operation(id: string) {
			return (
				await apiClient.get<null | {
					kind: "publish" | "restore" | "save" | "undo";
					operationId: string;
					revision?: string;
					version: number;
				}>(`${path}/operations/${encodeURIComponent(id)}`, options)
			).data;
		},
		async publish(
			expectedVersion: number,
			publicationId: string,
			confirmDeletions: boolean,
		) {
			return (
				await apiClient.post<{ revision: string }>(
					`${path}/publish`,
					{ confirmDeletions, expectedVersion, publicationId },
					options,
				)
			).data;
		},
		async read() {
			const value = (await apiClient.get<ConfigurationSnapshot>(path, options))
				.data;
			return checkSnapshot(value);
		},
		async restore(
			expectedVersion: number,
			operationId: string,
			revision: string,
		) {
			return checkSnapshot(
				(
					await apiClient.post<ConfigurationSnapshot>(
						`${path}/restore`,
						{ expectedVersion, operationId, revision },
						options,
					)
				).data,
			);
		},
		async revision(id: string) {
			return (
				await apiClient.get<{
					draft: EdgeConfigurationDraft;
					revision: string;
					sourceUrls: Record<string, string>;
					unreadableSourceUrls: string[];
				}>(`${path}/revisions/${encodeURIComponent(id)}`, options)
			).data;
		},
		async save(
			expectedVersion: number,
			operationId: string,
			configuration: EdgeConfigurationDraft,
		) {
			return checkSnapshot(
				(
					await apiClient.put<ConfigurationSnapshot>(
						path,
						{ configuration, expectedVersion, operationId },
						options,
					)
				).data,
			);
		},
		async status() {
			return (
				await apiClient.get<
					Pick<ConfigurationSnapshot, "desiredRevision" | "reports" | "version">
				>(`${path}/status`, options)
			).data;
		},
		async undo(
			expectedVersion: number,
			operationId: string,
			removedBy: string,
		) {
			return checkSnapshot(
				(
					await apiClient.post<ConfigurationSnapshot>(
						`${path}/undo`,
						{ expectedVersion, operationId, removedBy },
						options,
					)
				).data,
			);
		},
	};
}
